import type { RefObject } from 'react';
import { useEffect } from 'react';

import { isOutsideClick } from '@repo/ui/utils/is-outside-click';

/**
 * Type for the callback function executed on an outside click.
 */
type Callback = (event: MouseEvent) => void;

/**
 * Custom hook to detect clicks outside of a specified set of elements.
 *
 * @template T The type of the element(s) the refs point to. Can include null.
 * @param {RefObject<T>[]} refs An array of React RefObjects pointing to the elements to monitor.
 *                                Accepts refs where `.current` might be null (e.g., initialized with `useRef(null)`).
 * @param {Callback} callback The function to call when a click occurs outside all referenced elements.
 */
export function useOutsideClick<T extends HTMLElement | null>(refs: RefObject<T>[], callback: Callback): void {
  useEffect(() => {
    // Defines the event listener that will be added to the document.
    const listener = (event: MouseEvent) => {
      // Uses the isOutsideClick utility to determine if the click
      // originated outside all elements referenced by the 'refs' array.
      if (isOutsideClick(event.target, refs)) {
        // If the click is outside, invoke the provided callback function.
        callback(event);
      }
    };

    // Attaches the event listener to the 'mousedown' event on the document.
    // 'mousedown' is often preferred over 'click' to catch the interaction
    // before potential DOM changes triggered by the click itself.
    document.addEventListener('mousedown', listener);

    // Cleanup function returned by useEffect.
    // This runs when the component unmounts or before the effect re-runs
    // due to dependency changes.
    return () => {
      // Removes the event listener from the document to prevent memory leaks.
      document.removeEventListener('mousedown', listener);
    };
    // Dependency array for the useEffect hook.
    // The effect will re-run if the identity of the 'refs' array
    // or the 'callback' function changes.
  }, [refs, callback]);
}
